import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useOnCall } from 'hooks/useOnCall';
import { useReleaseCheck } from 'hooks/useReleaseCheck';
import UpdateBanner from './UpdateBanner';
import UpdateRequired from './UpdateRequired';

// "Later" hides the banner until the next deploy: the dismissed build is remembered for this tab.
const DISMISSED_KEY = 'releaseUpdateDismissed';

const readDismissed = () => {
    try {
        return sessionStorage.getItem(DISMISSED_KEY);
    } catch (error) {
        return null;
    }
};

const restart = () => window.location.reload();

// Shows the optional update banner or the required update page for a newer deployed build. Neither
// shows during a call; a required update takes over the screen as soon as the call ends.
const ReleaseCheck = ({ children }) => {
    const update = useReleaseCheck();
    const onCall = useOnCall();
    const [dismissed, setDismissed] = useState(readDismissed);

    if (update?.required && !onCall) {
        return (
            <UpdateRequired
                message={update.message}
                runningVersion={process.env.REACT_APP_VERSION}
                deployedVersion={update.deployedVersion}
                onRestart={restart}
            />
        );
    }

    const dismiss = () => {
        try {
            sessionStorage.setItem(DISMISSED_KEY, update.deployedVersion);
        } catch (error) {
            // Storage unavailable — hidden until this tab reloads.
        }
        setDismissed(update.deployedVersion);
    };

    const showBanner =
        update && !update.required && !onCall && dismissed !== update.deployedVersion;

    return (
        <>
            {children}
            {showBanner && (
                <UpdateBanner
                    message={update.message}
                    onRestart={restart}
                    onDismiss={dismiss}
                />
            )}
        </>
    );
};

ReleaseCheck.propTypes = {
    children: PropTypes.node
};

export default ReleaseCheck;
